import { useSnackbar } from "notistack";
import { api } from "./base-api";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { RetryTransaction } from "./auth/store";

//T is the type of the request body (the data parameter).
//R is the type of the response (what you expect to get back from the API).
export const useGet = () => {
  const errorHandler = useErrorHandler();
  const get = async <T>(
    url: string,
    onSuccess?: (response: T | null) => void,
    queSnackbar: boolean = true
  ): Promise<T> => {
    try {
      const response = await api.get<T>(url);
      if (onSuccess) {
        onSuccess(response.data);
      }
      return response.data;
    } catch (error) {
      const transaction: RetryTransaction = {
        url: url,
        data: undefined,
        config: undefined,
        method: "get",
      };
      return (await errorHandler).process(error, transaction, queSnackbar);
    }
  };
  return { get };
};

//T is the type of the request body (the data parameter).
//R is the type of the response (what you expect to get back from the API).
export const usePut = () => {
  const { enqueueSnackbar } = useSnackbar();
  const errorHandler = useErrorHandler();
  const put = async <T, R>(
    url: string,
    data: T,
    queSnackbar: boolean = true,
    config: AxiosRequestConfig = {}
  ): Promise<R> => {
    try {
      const isFormData = data instanceof FormData;
      const response = await api.put<R>(url, data, {
        ...config,
        headers: {
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
          ...config.headers,
        },
      });

      if (queSnackbar) {
        enqueueSnackbar("Successfully updated!", { variant: "success" });
      }
      return response.data;
    } catch (error) {
      const transaction: RetryTransaction = {
        url: url,
        data: data,
        config: config,
        method: "put",
      };
      return (await errorHandler).process(error, transaction);
    }
  };
  return { put };
};

export const usePost = () => {
  const { enqueueSnackbar } = useSnackbar();
  const post = async <T, R>(
    url: string,
    data: T,
    queSnackbar: boolean = true,
    config: AxiosRequestConfig = {}
  ): Promise<R> => {
    try {
      let response;
      if (config) {
        response = await api.post<R>(url, data, config);
      } else {
        response = await api.post<R>(url, data);
      }
      if (queSnackbar) {
        enqueueSnackbar(`Successfully saved!`, { variant: "success" });
      }
      return response.data;
    } catch (error) {
      if (queSnackbar) {
        enqueueSnackbar(errorMessageBuilder(error), { variant: "error" });
      }
      throw error;
    }
  };
  return { post };
};

export const useDelete = () => {
  const { enqueueSnackbar } = useSnackbar();
  const deleteRequest = async <R>(url: string): Promise<R> => {
    try {
      const response = await api.delete<R>(url);
      enqueueSnackbar(`Successfully saved!`, { variant: "success" });
      return response.data;
    } catch (error) {
      enqueueSnackbar(errorMessageBuilder(error), { variant: "error" });
      throw error;
    }
  };
  return { deleteRequest };
};

const errorMessageBuilder = (error: any) => {
  if (axios.isAxiosError(error)) {
    if (error.response?.data?.errors) {
      ///Handle Fluent Validation
      var errors = error.response?.data?.errors;

      const allErrors: string[] = [];
      for (const key in errors) {
        // Collect all string messages from each error array
        allErrors.push(...errors[key]);
      }
      return allErrors.join("; ") + ".";
    }
    return `Error (${error.status}): ${error.response?.data}`;
  } else {
    return `Error (${error.status}): ${error.message}`;
  }
};

const useErrorHandler = async () => {
  const { enqueueSnackbar } = useSnackbar();
  const process = (
    error: any,
    transaction: RetryTransaction,
    queSnackbar: boolean = true
  ) => {
    if (queSnackbar) {
      enqueueSnackbar(errorMessageBuilder(error), { variant: "error" });
    }
    throw error;
  };

  return { process };
};
